export const enhanceTemplateWithLoopWrappers = (template, templateLines = []) => {
  const lines = template.split('\n');
  const enhancedLines = [];
  const loopStack = [];
  let loopDepth = 0;

  // ============================================================
  // STEP 1: Analyze template to detect Case 2
  // ============================================================
  let hasContentBeforeLoop = false;
  let hasContentAfterLoop = false;
  let foundForLoop = false;
  let outerLoopStartLine = -1;
  let outerLoopEndLine = -1;

  const trimmedLines = lines
    .map((l) => (l.trim ? l.trim() : String(l).trim()))
    .filter((l) => l.length > 0);

  const firstLine = trimmedLines[0] || '';
  const lastLine = trimmedLines[trimmedLines.length - 1] || '';
  const isOnlyForLoop =
    firstLine.startsWith('{% for') && lastLine === '{% endfor %}';

  let hasRootContent = false;
  let insideLoop = false;
  let loopDepthCount = 0;

  for (const line of lines) {
    const trimmed = line.trim ? line.trim() : String(line).trim();
    if (!trimmed) continue;

    const isForStart = /{%\s*for\s+[a-zA-Z_]+\s+in\s+[^%]+\s*%}/.test(trimmed);
    const isForEnd = trimmed === '{% endfor %}' || trimmed === '{%endfor%}';

    if (isForStart) {
      loopDepthCount++;
      insideLoop = true;
      continue;
    }

    if (isForEnd) {
      loopDepthCount--;
      if (loopDepthCount === 0) insideLoop = false;
      continue;
    }

    if (loopDepthCount === 0 && !insideLoop) {
      const isTemplateTag =
        trimmed.startsWith('{%') || trimmed.startsWith('{#');
      if (!isTemplateTag && trimmed.length > 0) {
        hasRootContent = true;
      }
    }
  }

  console.log('[enhanceTemplateWithLoopWrappers] isOnlyForLoop:', isOnlyForLoop);
  console.log('[enhanceTemplateWithLoopWrappers] hasRootContent:', hasRootContent);

  const isCase2 = hasRootContent;
  console.log('[enhanceTemplateWithLoopWrappers] isCase2:', isCase2);

  // ============================================================
  // Build a map of template lines by content
  // ============================================================
  const templateLinesByContent = new Map();
  if (templateLines && Array.isArray(templateLines)) {
    templateLines.forEach((line) => {
      const trimmed = (line.content || '').trim
        ? (line.content || '').trim()
        : String(line.content || '').trim();
      if (trimmed) {
        if (!templateLinesByContent.has(trimmed)) {
          templateLinesByContent.set(trimmed, []);
        }
        templateLinesByContent.get(trimmed).push(line);
      }
    });
  }

  const contentOccurrenceCount = new Map();

  // ============================================================
  // ✅ DEV-ONLY GUARD — 3A (input state)
  // INVARIANT: hasRootContent and isCase2 must agree.
  //            isCase2 is derived from hasRootContent — nothing
  //            else may set it.
  //
  // Reason: the enhancer's wrapping decision (`isInner ||
  // (isCase2 && loopDepth === 1)`) and generateSelectorPathForRow's
  // `loopIsWrapped = isCase2 || loopDepth >= 1` MUST agree. If
  // either side computes isCase2 differently, path generation and
  // DOM shape diverge — every selector for depth-1 rows is off by
  // two segments.
  //
  // Owned by: this function. The mirrored decision lives in
  //           generateSelectorPathForRow (TemplateEditor.js).
  // ============================================================
  if (process.env.NODE_ENV !== 'production') {
    if (isCase2 !== hasRootContent) {
      console.warn(
        '[INVARIANT:3A] enhanceTemplateWithLoopWrappers: isCase2 diverged from hasRootContent.',
        { isCase2, hasRootContent },
      );
    }
  }

  const getMatchingTemplateLine = (trimmed) => {
    const entries = templateLinesByContent.get(trimmed) || [];
    if (entries.length === 0) {
      return null;
    }

    const currentOccurrence = contentOccurrenceCount.get(trimmed) || 0;
    contentOccurrenceCount.set(trimmed, currentOccurrence + 1);

    const index = Math.min(currentOccurrence, entries.length - 1);
    const matchedLine = entries[index];

    console.log(
      `[enhanceTemplateWithLoopWrappers] 🔍 Content "${trimmed}": occurrence ${
        currentOccurrence + 1
      }/${entries.length}, matched type: ${matchedLine?.type}, id: ${matchedLine?.id}`
    );

    return matchedLine || null;
  };

  // ============================================================
  // STEP 2: Enhanced template processing with type preservation
  // ============================================================
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim ? line.trim() : String(line).trim();

    const forMatch = trimmed.match(
      /{%\s*for\s+([a-zA-Z_][a-zA-Z0-9_]*)\s+in\s+([^%]+)\s*%}/
    );
    if (forMatch) {
      const varName = forMatch[1];
      const isInner = loopDepth > 0;

      loopStack.push({ varName, isInner, startLine: i });
      loopDepth++;

      const shouldWrap = isInner || (isCase2 && loopDepth === 1);

      if (shouldWrap) {
        const indent = line.match(/^(\s*)/)?.[1] || '';
        const loopType = isInner ? 'inner' : 'outer';

        // ============================================================
        // Calculate items per iteration by counting the body lines.
        // ============================================================
        let itemsPerIteration = 0;
        let depth = 0;
        let foundEnd = false;
        let loopEndLine = -1;

        for (let j = i + 1; j < lines.length && !foundEnd; j++) {
          const nextLine = lines[j];
          const nextTrimmed = nextLine.trim
            ? nextLine.trim()
            : String(nextLine).trim();
          if (/{%\s*for\s+/.test(nextTrimmed)) {
            depth++;
          } else if (/{%\s*endfor\s*%}/.test(nextTrimmed)) {
            if (depth === 0) {
              foundEnd = true;
              loopEndLine = j;
              break;
            } else {
              depth--;
            }
          }
        }

        if (foundEnd && loopEndLine !== -1) {
          const bodyLines = lines.slice(i + 1, loopEndLine);
          for (const bodyLine of bodyLines) {
            const trimmedBody = bodyLine.trim
              ? bodyLine.trim()
              : String(bodyLine).trim();
            if (
              !trimmedBody.startsWith('{%') &&
              !trimmedBody.startsWith('{#')
            ) {
              itemsPerIteration++;
            }
          }
          console.log(
            `[enhanceTemplateWithLoopWrappers] 📊 itemsPerIteration (no separator) for ${
              isInner ? 'INNER' : 'OUTER'
            } loop at line ${i}: ${itemsPerIteration}`
          );
        }

        // ============================================================
        // ✅ DEV-ONLY GUARD — 3A (itemsPerIteration sanity)
        // INVARIANT: itemsPerIteration must be >= 1 for a wrapped loop.
        //
        // Reason: itemsPerIteration drives modulo arithmetic in the
        // legacy code path and directly controls which rows belong to
        // which iteration. A zero or negative value silently produces
        // empty or overlapping iterations.
        //
        // Note: the fallback `= 4` below masks the "found zero body
        // lines" case. Warn before the fallback so the underlying
        // cause is visible.
        // ============================================================
        if (process.env.NODE_ENV !== 'production') {
          if (!foundEnd || loopEndLine === -1) {
            console.warn(
              '[INVARIANT:3A] enhanceTemplateWithLoopWrappers could not find matching endfor for loop.',
              {
                loopStartLine: i,
                loopVar: varName,
                isInner,
              },
            );
          } else if (!Number.isInteger(itemsPerIteration) || itemsPerIteration < 1) {
            console.warn(
              '[INVARIANT:3A] enhanceTemplateWithLoopWrappers computed non-positive itemsPerIteration.',
              {
                itemsPerIteration,
                loopStartLine: i,
                loopVar: varName,
                isInner,
                loopEndLine,
              },
            );
          }
        }

        if (itemsPerIteration === 0) {
          itemsPerIteration = 4;
        }

        console.log(
          `[enhanceTemplateWithLoopWrappers] 📊 Final itemsPerIteration for loop at line ${i}: ${itemsPerIteration}`
        );

        enhancedLines.push(
          `${indent}<div class="for-loop-started" data-loop-type="${loopType}" data-loop-var="${varName}" data-items-per-iteration="${itemsPerIteration}" data-wrap-iterations="true">`
        );
      }

      enhancedLines.push(line);

      const shouldEmitInlineIterWrapper = shouldWrap && (isCase2 || isInner);
      if (shouldEmitInlineIterWrapper) {
        const indent = line.match(/^(\s*)/)?.[1] || '';
        enhancedLines.push(
          `${indent}<div data-iter-id="{{ loop.index0 if loop is defined else __iterIndex }}">`
        );
      }
      continue;
    }

    const endForMatch = trimmed.match(/{%\s*endfor\s*%}/);
    if (endForMatch && loopStack.length > 0) {
      const loopInfo = loopStack.pop();
      loopDepth--;

      const shouldClose = loopInfo.isInner || (isCase2 && loopDepth === 0);

      const shouldEmitInlineIterClose = shouldClose && (isCase2 || loopInfo.isInner);
      if (shouldEmitInlineIterClose) {
        const indent = line.match(/^(\s*)/)?.[1] || '';
        enhancedLines.push(`${indent}</div>`);
      }

      enhancedLines.push(line);

      if (shouldClose) {
        const indent = line.match(/^(\s*)/)?.[1] || '';
        enhancedLines.push(`${indent}</div>`);
      }
      continue;
    }

    // ============================================================
    // Add type-preserving wrappers
    // ============================================================
    if (!trimmed.startsWith('{%') && !trimmed.startsWith('{#')) {
      const matchedLine = getMatchingTemplateLine(trimmed);

      if (matchedLine) {
        const type = matchedLine.type || 'text';
        const id = matchedLine.id || `unknown-${i}`;
        const indent = line.match(/^(\s*)/)?.[1] || '';

        if (!trimmed.includes('<div') && !trimmed.includes('</div>')) {
          console.log(
            `[enhanceTemplateWithLoopWrappers] 📝 Wrapping "${trimmed}" with type: ${type}, id: ${id}`
          );
          enhancedLines.push(
            `${indent}<div data-template-type="${type}" data-template-id="${id}">${trimmed}</div>`
          );
          continue;
        }
      }
    }

    enhancedLines.push(line);
  }

  const output = enhancedLines.join('\n');

  // ============================================================
  // ✅ DEV-ONLY GUARD — 3A (output shape)
  // INVARIANT: Each `{% for %}` in the input that should be wrapped
  //            produces exactly ONE opening
  //            `<div class="for-loop-started"` tag in the output.
  //
  // Reason: every downstream consumer (generateSelectorPathForRow,
  // applyStyleToAllInLoop, applyMediaToAllInLoop, applyCardsToWrappedLoop)
  // treats one for-loop-started div as the sole container for a
  // loop's iterations. If two are emitted, or none for a wrapped
  // loop, positions collapse or duplicate.
  //
  // The expected count is `#(for-loops) - #(outer loops in Case 1/3)`
  // because Case 1/3 outer loops are intentionally not wrapped.
  //
  // Owned by: this function.
  // ============================================================
  if (process.env.NODE_ENV !== 'production') {
    // Count all for-loops in the input.
    const inputForLoops = (template.match(/{%\s*for\s+/g) || []).length;

    // Count how many of them we intended to wrap.
    // Every loop wraps EXCEPT the outer loop of Case 1/3.
    // In Case 2 the outer loop wraps too.
    const outerLoopsToSkip = isCase2 ? 0 : 1; // 1 outer loop if !isCase2
    const expectedWrappers = Math.max(0, inputForLoops - outerLoopsToSkip);

    // Count output wrappers.
    const outputWrappers = (
      output.match(/class="for-loop-started"/g) || []
    ).length;

    if (expectedWrappers !== outputWrappers) {
      console.warn(
        '[INVARIANT:3A] enhanceTemplateWithLoopWrappers produced an unexpected number of for-loop-started wrappers.',
        {
          inputForLoops,
          outerLoopsToSkip,
          expectedWrappers,
          outputWrappers,
          isCase2,
          hasRootContent,
        },
      );
    }

    // Guard against loops that were meant to wrap but produced no wrapper.
    if (expectedWrappers > 0 && outputWrappers === 0) {
      console.warn(
        '[INVARIANT:3A] enhanceTemplateWithLoopWrappers produced zero wrappers despite expected wrappers.',
        { inputForLoops, expectedWrappers, isCase2 },
      );
    }

    // Guard against double-wrapping.
    if (outputWrappers > inputForLoops) {
      console.warn(
        '[INVARIANT:3A] enhanceTemplateWithLoopWrappers produced more wrappers than input for-loops — likely double-wrapped.',
        { inputForLoops, outputWrappers },
      );
    }
  }

  return output;
};